import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { isVerifiedFromFmcsa } from "@/lib/fmcsaVerify";
import { formatEin, isEinVerifiedFromFmcsa } from "@/lib/ein";
import { extractDigits, formatPhoneForDisplay } from "@/lib/phone";
import { ShieldCheck, RefreshCcw, Loader2, AlertTriangle, Truck, Users } from "lucide-react";
import { saferBadgeClasses } from "@/lib/saferBadge";

function statusChipClass(status) {
    // Phase N.14 (2026-02) — delegate to the shared helper so
    // ``ACTIVE · NOT AUTHORIZED`` renders amber (caution), not emerald
    // (safe). See ``lib/saferBadge.js``.
    return saferBadgeClasses(status);
}

function displayStatus(snap) {
    const parts = [];
    if (snap.operating_status) parts.push(snap.operating_status.toUpperCase());
    if (snap.operating_authority_status) parts.push(snap.operating_authority_status);
    return parts.join(" · ");
}

/**
 * FMCSA SAFER lookup, reusable across submission form + admin customer pages.
 *
 * Props:
 *  - usdot         : string current USDOT number typed by the user
 *  - onPrefill     : (partial) => void — called with company_name/company_address/company_phone
 *  - companyValues : { company_name, company_address, company_phone } — to detect conflicts on "Use SAFER data"
 *  - autoLookup    : bool (default true) — auto-fetch on change/blur when value is 5–8 digits
 *  - disabled      : bool — hides verify action (locked view)
 *  - testIdPrefix  : string for data-testid
 */
export const FmcsaLookupCard = forwardRef(function FmcsaLookupCard({
    usdot,
    onPrefill,
    companyValues = {},
    autoLookup = true,
    disabled = false,
    testIdPrefix = "fmcsa",
}, ref) {
    const [loading, setLoading] = useState(false);
    const [snap, setSnap] = useState(null);
    const [qcm, setQcm] = useState(null);  // Phase QCM — parallel QCMobile snapshot
    const [error, setError] = useState(null);
    const [cacheHeader, setCacheHeader] = useState(null);
    const [lastLookedUp, setLastLookedUp] = useState("");
    const debounceRef = useRef(null);

    const isValid = /^\d{5,8}$/.test(usdot || "");

    const runLookup = useCallback(        async ({ refresh = false } = {}) => {
            if (!isValid) return;
            if (!refresh && usdot === lastLookedUp && (snap || error)) return;
            setLoading(true);
            setError(null);
            try {
                // Phase QCM — fetch both surfaces in parallel. SAFER stays as
                // the legacy "verify" payload (status string + snapshot date);
                // QCMobile is the source-of-truth for EIN + MC# + safety data.
                const [legacy, qcmResp] = await Promise.allSettled([
                    api.get("/fmcsa/lookup", { params: { usdot, refresh: refresh ? 1 : 0 } }),
                    api.get(`/public/fmcsa/snapshot/${usdot}`),
                ]);
                if (legacy.status === "fulfilled") {
                    setSnap(legacy.value.data);
                    setCacheHeader(legacy.value.headers?.["x-cache"] || null);
                } else {
                    throw legacy.reason;
                }
                if (qcmResp.status === "fulfilled") setQcm(qcmResp.value.data);
                else setQcm(null);
                setLastLookedUp(usdot);
            } catch (err) {
                setSnap(null);
                const status = err?.response?.status;
                if (status === 404) {
                    setError({ kind: "not_found", msg: "No carrier found for this USDOT in FMCSA SAFER" });
                } else if (status === 503) {
                    setError({
                        kind: "unavailable",
                        msg: "FMCSA SAFER is temporarily unavailable — you can still submit without verification",
                    });
                } else if (status === 400) {
                    setError({ kind: "invalid", msg: "USDOT must be 5–8 digits" });
                } else {
                    setError({ kind: "generic", msg: formatApiError(err) || "Verification failed" });
                }
                setLastLookedUp(usdot);
            } finally {
                setLoading(false);
            }
        },
        [usdot, isValid, lastLookedUp, snap, error]
    );

    // Auto-lookup (debounced) when value changes & is valid
    useEffect(() => {
        if (!autoLookup || disabled || !isValid) return;
        if (usdot === lastLookedUp) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runLookup(), 500);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [usdot, autoLookup, disabled, isValid, lastLookedUp, runLookup]);

    // Clear snapshot if user types and value no longer matches what we last fetched
    useEffect(() => {
        if (snap && snap.usdot_number && usdot !== snap.usdot_number) {
            setSnap(null);
            setError(null);
        }
    }, [usdot, snap]);

    // Phase QCM-EXT followup — imperative handle for the CollapsibleSection
    // wrapper on AdminCustomerDetailPage. `refresh()` triggers the same
    // ``refresh:true`` lookup the in-card button does; ``isLoading`` lets
    // the parent disable the duplicate header button.
    useImperativeHandle(ref, () => ({
        refresh: () => runLookup({ refresh: true }),
        get isLoading() { return loading; },
        get isValid() { return isValid; },
        get hasSnapshot() { return Boolean(snap); },
    }), [runLookup, loading, isValid, snap]);

    const onUseSaferData = () => {
        if (!snap) return;
        // Phase QCM — prefer QCMobile data when available (it's structured),
        // fall back to the SAFER legacy fields. EIN comes ONLY from QCMobile.
        const candidate = {
            company_name: (qcm?.legal_name || snap.legal_name) || "",
            company_address: ((qcm && qcm.physical_address)
                ? [qcm.physical_address.street, qcm.physical_address.city, qcm.physical_address.state, qcm.physical_address.zip].filter(Boolean).join(", ")
                : (snap.physical_address || "")).replace(/\n/g, ", "),
            company_phone: extractDigits(qcm?.phone || snap.phone || ""),
            ein_tax_id: qcm?.ein ? formatEin(qcm.ein) : "",
        };
        const conflicts = [];
        if (companyValues.company_name && candidate.company_name && companyValues.company_name !== candidate.company_name) conflicts.push("Company Name");
        if (companyValues.company_address && candidate.company_address && companyValues.company_address !== candidate.company_address) conflicts.push("Company Address");
        if (companyValues.company_phone && candidate.company_phone && companyValues.company_phone !== candidate.company_phone) conflicts.push("Company Phone");
        if (conflicts.length > 0) {
            const ok = window.confirm(`This will replace the values you've entered in ${conflicts.join(", ")}. Continue?`);
            if (!ok) return;
        }
        // Build patch — only include EIN when we actually have one so we
        // don't clobber a customer-typed EIN with an empty string.
        const patch = {
            company_name: candidate.company_name || companyValues.company_name || "",
            company_address: candidate.company_address || companyValues.company_address || "",
            company_phone: candidate.company_phone || companyValues.company_phone || "",
        };
        if (candidate.ein_tax_id) patch.ein_tax_id = candidate.ein_tax_id;
        onPrefill?.(patch);
    };

    if (disabled) return null;

    return (
        <div className="mt-3 space-y-3" data-testid={`${testIdPrefix}-wrapper`}>
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => runLookup({ refresh: true })}
                    disabled={!isValid || loading}
                    className="gap-1.5"
                    data-testid={`${testIdPrefix}-verify`}
                >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                    {loading ? "Verifying…" : snap ? "Re-verify with FMCSA" : "Verify with FMCSA"}
                </Button>
                {!isValid && (usdot || "").length > 0 && (
                    <span className="text-xs text-slate-500">Enter 5–8 digits to verify</span>
                )}
                {cacheHeader && (
                    <span className="text-[10px] uppercase tracking-wider text-slate-400" data-testid={`${testIdPrefix}-cache`}>
                        {cacheHeader === "hit" ? "cached" : "live"}
                    </span>
                )}
            </div>

            {error && (
                <div
                    className={`rounded-md border p-3 text-sm flex items-start gap-2 ${
                        error.kind === "unavailable"
                            ? "border-amber-200 bg-amber-50 text-amber-900"
                            : "border-red-200 bg-red-50 text-red-800"
                    }`}
                    data-testid={`${testIdPrefix}-error`}
                >
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>{error.msg}</span>
                </div>
            )}

            {snap && (
                <div
                    className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
                    data-testid={`${testIdPrefix}-card`}
                >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold text-slate-900 text-base" data-testid={`${testIdPrefix}-legal-name`}>
                                    {snap.legal_name || "—"}
                                </h4>
                                <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${statusChipClass(
                                        // Phase N.14.1 (2026-02) — pass the FULL composed label
                                        // (``operating_status · operating_authority_status``) so the
                                        // classifier can see ``"NOT AUTHORIZED"`` when it's present.
                                        // Passing just ``snap.operating_status`` (the pre-fix shape)
                                        // hid the NOT AUTHORIZED signal → emerald rendered for
                                        // ``ACTIVE · NOT AUTHORIZED`` carriers. See ``lib/saferBadge.js``.
                                        displayStatus(snap)
                                    )}`}
                                    data-testid={`${testIdPrefix}-status`}
                                >
                                    {displayStatus(snap) || "STATUS UNKNOWN"}
                                </span>
                            </div>
                            {snap.dba_name && (
                                <p className="text-xs text-slate-500 mt-0.5" data-testid={`${testIdPrefix}-dba`}>
                                    DBA: {snap.dba_name}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => runLookup({ refresh: true })}
                            className="text-xs text-slate-500 hover:text-blue-900 inline-flex items-center gap-1"
                            data-testid={`${testIdPrefix}-refresh`}
                        >
                            <RefreshCcw size={12} /> Refresh
                        </button>
                    </div>

                    {snap.out_of_service_date && (
                        <div className="mt-2 rounded-md bg-red-50 border border-red-100 px-3 py-1.5 text-xs text-red-800">
                            Out of service since <strong>{snap.out_of_service_date}</strong>
                        </div>
                    )}

                    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                        {snap.physical_address && (
                            <div>
                                <dt className="text-xs text-slate-500">Physical address</dt>
                                <dd className="text-slate-900 whitespace-pre-line">{snap.physical_address}</dd>
                            </div>
                        )}
                        {snap.entity_type && (
                            <div>
                                <dt className="text-xs text-slate-500">Entity type</dt>
                                <dd className="text-slate-900">{snap.entity_type}</dd>
                            </div>
                        )}
                        {snap.mc_mx_ff_number && (
                            <div>
                                <dt className="text-xs text-slate-500">MC/MX/FF</dt>
                                <dd className="text-slate-900 font-mono text-xs">{snap.mc_mx_ff_number}</dd>
                            </div>
                        )}
                        {qcm?.ein && (
                            <div data-testid={`${testIdPrefix}-ein-block`}>
                                <dt className="text-xs text-slate-500">EIN</dt>
                                <dd className="text-slate-900 font-mono text-sm flex items-center gap-2">
                                    {formatEin(qcm.ein)}
                                    {/* Phase QCM follow-up — badge only shows
                                        while the current form value matches
                                        the FMCSA-sourced EIN. If the user
                                        overrides the field, the badge
                                        disappears; restoring the original
                                        value brings it back.
                                        Phase QCM-EIN — comparison is
                                        digit-only so "20-8148260" matches
                                        FMCSA's raw 208148260. */}
                                    {isEinVerifiedFromFmcsa(companyValues.ein_tax_id, qcm.ein) && (
                                        <span
                                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800"
                                            data-testid={`${testIdPrefix}-ein-verified-badge`}
                                        >
                                            <ShieldCheck size={10} /> Verified from FMCSA
                                        </span>
                                    )}
                                </dd>
                            </div>
                        )}
                        {snap.phone && (
                            <div>
                                <dt className="text-xs text-slate-500">Phone</dt>
                                <dd className="text-slate-900">{formatPhoneForDisplay(snap.phone) || snap.phone}</dd>
                            </div>
                        )}
                    </dl>

                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                        {typeof snap.power_units === "number" && (
                            <span className="inline-flex items-center gap-1">
                                <Truck size={12} /> {snap.power_units.toLocaleString()} trucks
                            </span>
                        )}
                        {typeof snap.drivers === "number" && (
                            <span className="inline-flex items-center gap-1">
                                <Users size={12} /> {snap.drivers.toLocaleString()} drivers
                            </span>
                        )}
                        {snap.safer_snapshot_date && (
                            <span className="ml-auto">Per SAFER on {snap.safer_snapshot_date}</span>
                        )}
                    </div>

                    {onPrefill && (
                        <div className="mt-4 flex justify-end">
                            <Button
                                type="button"
                                size="sm"
                                onClick={onUseSaferData}
                                className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                                data-testid={`${testIdPrefix}-use`}
                            >
                                Use SAFER data to fill company info
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default FmcsaLookupCard;
